import { ProgramRouteObservation } from './program-route';
import { PROGRAM_ROUTE_PROCESSOR } from './worklet-protocol';
import type { AudioFilterDefinition, AudioFilterRuntime } from '../core/definitions';
import type { AudioSettings, AudioStrip, FilterInstance, Id, SettingsBag } from '../core/model';
import type { Unsubscribe } from '../core/services';
import { MASTER_STRIP, type AudioEngine, type AudioEngineDeps, type LevelReading } from './api';
import { ClipHold, applyMeterLevels, meterReportQuanta } from './dsp/meter-report';
import { numberSetting, rampTo, smoothTo } from './params';
import { createStereoMatrix, type StereoMatrix } from './stereo-matrix';
import { METER_PROCESSOR, WORKLET_CHANNELS, type WorkletMessage } from './worklet-protocol';
import { loadWorklets } from './worklets';
import { createMonitorOutput, type MonitorOutput } from './monitorOutput';

/**
 * Threading. Everything that produces program audio runs on the audio render
 * thread and keeps running when the main thread is throttled, blocked or the
 * tab is hidden: the node graph, the worklet DSP, and every gain change, which
 * is scheduled as AudioParam automation at the moment of the call (no timers,
 * no requestAnimationFrame anywhere in this module's audio path).
 *
 * Main-thread dependent, and therefore late (never wrong) under throttling:
 *  - meter readings and getQuantaCount(): MessagePort reports from the worklets;
 *  - applying a new call (setStrip, setFilters, setProgramMix, applySettings):
 *    takes effect when the caller's code runs, then proceeds on the audio thread;
 *  - async filter creation: audio uses the previous chain until it resolves;
 *  - context state handling and auto-resume: driven by statechange,
 *    visibilitychange and devicechange events;
 *  - the monitor context's resume/suspend and device selection (monitoring
 *    only; the program bus does not pass through it, see monitorOutput.ts);
 *  - resume()'s give-up timer, which only bounds how long the caller waits.
 */
const LOG_SCOPE = 'audio';
const DEFAULT_SAMPLE_RATE = 48000;
const MAX_SYNC_OFFSET_MS = 20000;
/** DelayNode buffers are allocated up front, so strips start small and grow on demand. */
const INITIAL_DELAY_CAPACITY_S = 1;
/** Shortest program-mix ramp; a hard cut would click. */
const DECLICK_MS = 5;
/** Upper bound for fader gains (+26 dB), far above anything the fader law produces. */
const MAX_GAIN = 20;
/** `LevelReading.clipped` stays true for this much audio time after the last full-scale sample. */
const CLIP_HOLD_MS = 1000;
/**
 * `context.resume()` stays pending for as long as the browser withholds
 * permission (no user activation); callers of `resume()` get an answer after
 * this long at the latest and read the outcome from the state.
 */
const RESUME_TIMEOUT_MS = 1000;
/**
 * A context that drops out of `running` again right after every resume (e.g.
 * its output device keeps failing) gets this many automatic attempts per
 * window; after that only the external triggers retry.
 */
const AUTO_RESUME_LIMIT = 3;
const AUTO_RESUME_WINDOW_MS = 10_000;

const DEFAULT_STRIP: AudioStrip = {
  volume: 1,
  muted: false,
  balance: 0,
  forceMono: false,
  syncOffsetMs: 0,
  monitoring: 'off',
};

interface MeterTap {
  node: AudioWorkletNode | null;
  reading: LevelReading;
  /** Keeps `reading.clipped` on for CLIP_HOLD_MS after a clip, whoever reads it and however often. */
  clipHold: ClipHold;
  /** Render quanta this meter's processor had counted at its latest report. */
  quanta: number;
}

interface FilterSlot {
  id: Id;
  kind: string;
  /** Identity of the instance settings last seen, to detect changes. */
  settings: SettingsBag;
  /** Definition defaults overlaid with `settings`; what the runtime receives. */
  resolved: SettingsBag;
  enabled: boolean;
  /** null while an async create is pending or after it failed. */
  runtime: AudioFilterRuntime | null;
}

type Edge = readonly [from: AudioNode, to: AudioNode];

interface Strip {
  id: Id;
  source: AudioNode | null;
  input: GainNode;
  delay: DelayNode;
  delayCapacity: number;
  matrix: StereoMatrix;
  fader: GainNode;
  programSend: GainNode;
  programMix: GainNode;
  monitorSend: GainNode;
  meter: MeterTap;
  state: AudioStrip;
  /** False until the first setStrip; that one is applied without ramps. */
  configured: boolean;
  requested: readonly FilterInstance[];
  slots: Map<Id, FilterSlot>;
  order: FilterSlot[];
  /** Connections currently made between the delay, the active filters and the matrix. */
  edges: Edge[];
  routeEpoch:number;
  mixTarget: number;
  mixEndTime: number;
}

function silentReading(): LevelReading {
  return {
    peakDb: new Array<number>(WORKLET_CHANNELS).fill(-Infinity),
    rmsDb: new Array<number>(WORKLET_CHANNELS).fill(-Infinity),
    clipped: false,
  };
}

function isThenable<T>(value: T | Promise<T>): value is Promise<T> {
  return typeof (value as Promise<T>).then === 'function';
}

class AudioEngineImpl implements AudioEngine {
  readonly context: AudioContext;

  private routeGeneration=0;private masterTarget=1;private masterGeneration=0;
  private outputObserver?:{close():void};
  private readonly strips = new Map<Id, Strip>();
  private readonly levels: Record<string, LevelReading> = {};
  private readonly stateListeners = new Set<(state: AudioContextState) => void>();

  private readonly masterBus: GainNode;
  private readonly masterFader: GainNode;
  private readonly masterMeter: MeterTap;
  private readonly programOut: MediaStreamAudioDestinationNode;

  private readonly monitorBus: GainNode;
  private readonly monitorOut: MediaStreamAudioDestinationNode;
  private readonly monitor: MonitorOutput;

  /** Set by the first resume(): from then on the engine works to keep the context running. */
  private wantRunning = false;
  private resumeRefusalLogged = false;
  private autoResumeTimes: number[] = [];
  private notifiedState: AudioContextState;

  /** null until setProgramMix is first called; until then every strip is in the mix at unity. */
  private programMix: Readonly<Record<Id, number>> | null = null;
  private workletsReady = false;
  private destroyPromise: Promise<void> | null = null;
  private readonly unsubscribeRegistry: Unsubscribe;

  constructor(private readonly deps: AudioEngineDeps) {
    const context = new AudioContext({
      sampleRate: deps.sampleRate ?? DEFAULT_SAMPLE_RATE,
      latencyHint: 'interactive',
    });
    this.context = context;

    this.masterBus = new GainNode(context, { channelCount: 2, channelCountMode: 'explicit' });
    this.masterFader = new GainNode(context);
    this.programOut = new MediaStreamAudioDestinationNode(context, { channelCount: 2, channelCountMode: 'explicit' });
    this.masterBus.connect(this.masterFader).connect(this.programOut);
    this.masterMeter = this.createMeterTap();
    this.levels[MASTER_STRIP] = this.masterMeter.reading;

    // The monitor bus plays through its own context rather than context.destination
    // so its device can change (setSinkId) and fail without touching the context
    // that clocks the program output.
    this.monitorBus = new GainNode(context, { channelCount: 2, channelCountMode: 'explicit' });
    this.monitorOut = new MediaStreamAudioDestinationNode(context, { channelCount: 2, channelCountMode: 'explicit' });
    this.monitorBus.connect(this.monitorOut);
    this.monitor = createMonitorOutput(deps.logger, this.monitorOut.stream, context.sampleRate);

    this.notifiedState = context.state;
    context.addEventListener('statechange', this.handleStateChange);
    context.addEventListener('error', this.handleContextError);
    // Moments at which a refused resume may now be granted, or the monitor device may have changed.
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    navigator.mediaDevices?.addEventListener('devicechange', this.handleDeviceChange);
    // A filter kind registered after its instances were set (plugins) is picked up here.
    this.unsubscribeRegistry = deps.registry.subscribe(() => {
      for (const strip of this.strips.values()) this.reconcileFilters(strip);
    });
  }

  async init(): Promise<void> {
    await loadWorklets(this.context);
    if (this.destroyPromise || this.workletsReady) return;
    this.workletsReady = true;
    this.attachMeter(this.masterMeter, this.masterFader);
    for (const strip of this.strips.values()) this.attachMeter(strip.meter, strip.fader);
  }

  /**
   * Ask for the context to run and keep running. Never rejects and never
   * hangs: when the browser refuses (no user activation yet) the state stays
   * as it is and the engine retries when the tab becomes visible, when the
   * device list changes, and whenever this is called again.
   */
  async resume(): Promise<void> {
    if (this.destroyPromise) return;
    this.wantRunning = true;
    await this.requestResume();
    this.updateMonitorPlayback();
  }

  getState(): AudioContextState {
    return this.context.state;
  }

  onStateChange(listener: (state: AudioContextState) => void): Unsubscribe {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  getOutputStream(): MediaStream {
    return this.programOut.stream;
  }

  private reactionTaps = new Map<Id, Set<() => void>>();

  openSourceTap(sourceId: Id): { track: MediaStreamTrack; close(): void } {
    const strip = this.strips.get(sourceId);
    if (this.destroyPromise || !strip?.source) throw new Error('Audio source unavailable');
    const destination = this.context.createMediaStreamDestination();
    strip.fader.connect(destination);
    const track = destination.stream.getAudioTracks()[0];
    let closed = false;
    const close = () => {
      if (closed) return;
      closed = true;
      this.disconnectEdge(strip.fader, destination);
      track.stop();
      const taps = this.reactionTaps.get(sourceId);
      taps?.delete(close);
      if (!taps?.size) this.reactionTaps.delete(sourceId);
    };
    const taps = this.reactionTaps.get(sourceId) ?? new Set<() => void>();
    taps.add(close); this.reactionTaps.set(sourceId, taps);
    return { track, close };
  }

  attachSource(sourceId: Id, node: AudioNode | null): void {
    if (this.destroyPromise) return;
    const strip = this.ensureStrip(sourceId);
    if (strip.source === node) return;
    for (const close of this.reactionTaps.get(sourceId) ?? []) close();
    this.detachSource(strip);
    if (!node) return;
    try {
      node.connect(strip.input);
      strip.source = node;
    } catch (error) {
      this.deps.logger.log('error', LOG_SCOPE, `Cannot connect audio of source ${sourceId}`, error);
    }
  }

  removeSource(sourceId: Id): void {
    const strip = this.strips.get(sourceId);
    if (!strip) return;
    for (const close of this.reactionTaps.get(sourceId) ?? []) close();
    this.strips.delete(sourceId);
    delete this.levels[sourceId];
    this.detachSource(strip);

    for (const [from, to] of strip.edges) this.disconnectEdge(from, to);
    strip.edges = [];
    for (const slot of strip.slots.values()) this.destroyRuntime(slot);
    strip.slots.clear();
    strip.order = [];

    this.detachMeter(strip.meter);
    strip.input.disconnect();
    strip.delay.disconnect();
    strip.matrix.destroy();
    strip.fader.disconnect();
    strip.programSend.disconnect();
    strip.programMix.disconnect();
    strip.monitorSend.disconnect();
    this.updateMonitorPlayback();
  }

  setStrip(sourceId: Id, state: AudioStrip): void {
    if (this.destroyPromise) return;
    const strip = this.ensureStrip(sourceId);
    strip.state = state;
    this.applyStrip(strip, !strip.configured);
    strip.configured = true;
    this.updateMonitorPlayback();
  }

  setFilters(sourceId: Id, filters: readonly FilterInstance[]): void {
    if (this.destroyPromise) return;
    const strip = this.ensureStrip(sourceId);
    strip.requested = filters;
    this.reconcileFilters(strip);
  }

  setProgramMix(gains: Readonly<Record<Id, number>>, rampMs: number): void {
    if (this.destroyPromise) return;
    this.programMix = gains;
    const seconds = Math.max(DECLICK_MS, Number.isFinite(rampMs) ? rampMs : 0) / 1000;
    const endTime = this.context.currentTime + seconds;
    for (const strip of this.strips.values()) {
      const target = this.mixGainFor(strip.id);
      // Repeating the same target must not stretch a ramp that is already on its way there.
      if (target === strip.mixTarget && endTime >= strip.mixEndTime) continue;
      strip.routeEpoch++;strip.mixTarget = target;
      strip.mixEndTime = endTime;
      rampTo(this.context, strip.programMix.gain, target, seconds);
    }
  }

  applySettings(settings: AudioSettings): void {
    if (this.destroyPromise) return;
    if (settings.sampleRate !== this.context.sampleRate) {
      this.deps.logger.log(
        'warn',
        LOG_SCOPE,
        `Sample rate ${settings.sampleRate} Hz takes effect after a restart; the engine runs at ${this.context.sampleRate} Hz`,
      );
    }
    const gain = settings.masterMuted ? 0 : numberSetting(settings.masterVolume, 1, 0, MAX_GAIN);
    if(gain!==this.masterTarget){this.masterTarget=gain;this.masterGeneration++;}
    smoothTo(this.context, this.masterFader.gain, gain);
    this.monitor.setDevice(settings.monitorDeviceId ?? '');
  }

  getLevels(): Readonly<Record<string, LevelReading>> {
    return this.levels;
  }

  getQuantaCount(): number {
    return this.masterMeter.quanta;
  }

  destroy(): Promise<void> {
    this.destroyPromise ??= this.teardown();
    return this.destroyPromise;
  }

  // -------------------------------------------------------------------------

  private async teardown(): Promise<void> {
    this.outputObserver?.close();
    this.unsubscribeRegistry();
    for (const id of [...this.strips.keys()]) this.removeSource(id);
    this.detachMeter(this.masterMeter);
    delete this.levels[MASTER_STRIP];

    // A monitor context that fails to close must not leave the rest of the engine open.
    const monitorClosed = this.monitor.destroy().catch((error: unknown) => {
      this.deps.logger.log('warn', LOG_SCOPE, 'The monitor output could not be closed', error);
    });
    this.masterBus.disconnect();
    this.masterFader.disconnect();
    this.monitorBus.disconnect();
    for (const stream of [this.programOut.stream, this.monitorOut.stream]) {
      for (const track of stream.getTracks()) track.stop();
    }

    this.context.removeEventListener('statechange', this.handleStateChange);
    this.context.removeEventListener('error', this.handleContextError);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    navigator.mediaDevices?.removeEventListener('devicechange', this.handleDeviceChange);
    if (this.context.state !== 'closed') await this.context.close();
    await monitorClosed;
    this.notifyState('closed');
    this.stateListeners.clear();
  }

  // ---- context lifecycle --------------------------------------------------

  private readonly handleStateChange = (): void => {
    const state = this.context.state;
    switch (state) {
      case 'running':
        this.resumeRefusalLogged = false;
        break;
      case 'suspended':
      case 'interrupted':
        // No meter reports arrive while the context is stopped; do not leave stale levels on screen.
        this.clearAllReadings();
        break;
      case 'closed':
        this.clearAllReadings();
        if (!this.destroyPromise) {
          this.deps.logger.log(
            'error',
            LOG_SCOPE,
            'The AudioContext was closed outside destroy(); the program audio track has ended and the engine must be recreated',
          );
        }
        break;
    }
    this.updateMonitorPlayback();
    this.notifyState(state);
    if (state === 'suspended' || state === 'interrupted') this.autoResume(state);
  };

  private notifyState(state: AudioContextState): void {
    if (state === this.notifiedState) return;
    this.notifiedState = state;
    for (const listener of [...this.stateListeners]) listener(state);
  }

  /** Nothing suspends the context on the engine's behalf, so leaving `running` after resume() is always unwanted. */
  private autoResume(state: AudioContextState): void {
    if (!this.wantRunning || this.destroyPromise) return;
    const now = performance.now();
    this.autoResumeTimes = this.autoResumeTimes.filter((time) => now - time < AUTO_RESUME_WINDOW_MS);
    if (this.autoResumeTimes.length >= AUTO_RESUME_LIMIT) {
      this.noteResumeRefused(`it keeps leaving the running state (${state})`);
      return;
    }
    this.autoResumeTimes.push(now);
    this.deps.logger.log('info', LOG_SCOPE, `AudioContext became ${state}; resuming`);
    void this.requestResume();
  }

  /** One resume attempt. Settles when the browser answers or after RESUME_TIMEOUT_MS, whichever is first. */
  private async requestResume(): Promise<void> {
    const before = this.getState();
    if (before === 'running' || before === 'closed') return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, RESUME_TIMEOUT_MS);
    });
    let reason = 'the browser has not allowed it yet';
    try {
      await Promise.race([this.context.resume(), timeout]);
    } catch (error) {
      reason = String(error);
    } finally {
      clearTimeout(timer);
    }
    const after = this.getState();
    if (after !== 'running' && after !== 'closed' && !this.destroyPromise) this.noteResumeRefused(reason);
  }

  private noteResumeRefused(reason: string): void {
    if (this.resumeRefusalLogged) return;
    this.resumeRefusalLogged = true;
    this.deps.logger.log(
      'warn',
      LOG_SCOPE,
      `AudioContext is ${this.getState()} and could not be resumed (${reason}); retrying on the next user gesture, when the tab becomes visible or when audio devices change`,
    );
  }

  /** Retry a refused resume at a moment the browser may now grant it. */
  private retryResume(): void {
    if (this.destroyPromise || !this.wantRunning) return;
    const state = this.getState();
    if (state === 'suspended' || state === 'interrupted') void this.requestResume();
    // The browser may also have suspended the monitor context in the meantime.
    this.updateMonitorPlayback();
  }

  private readonly handleContextError = (): void => {
    // Chromium reports a failing output device here and then suspends the context, which handleStateChange picks up.
    this.deps.logger.log('error', LOG_SCOPE, 'The AudioContext reported a rendering error (audio output device failure)');
  };

  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') this.retryResume();
  };

  private readonly handleDeviceChange = (): void => {
    this.retryResume();
    void this.monitor.reconcileDevice();
  };

  private ensureStrip(id: Id): Strip {
    const existing = this.strips.get(id);
    if (existing) return existing;

    const context = this.context;
    const input = new GainNode(context);
    const delay = new DelayNode(context, { maxDelayTime: INITIAL_DELAY_CAPACITY_S, delayTime: 0 });
    const matrix = createStereoMatrix(context);
    const fader = new GainNode(context);
    const programSend = new GainNode(context);
    const mixTarget = this.mixGainFor(id);
    const programMix = new GainNode(context, { gain: mixTarget });
    const monitorSend = new GainNode(context, { gain: 0 });

    input.connect(delay);
    matrix.output.connect(fader);
    fader.connect(programSend).connect(programMix).connect(this.masterBus);
    fader.connect(monitorSend).connect(this.monitorBus);

    const strip: Strip = {
      id,
      source: null,
      input,
      delay,
      delayCapacity: INITIAL_DELAY_CAPACITY_S,
      matrix,
      fader,
      programSend,
      programMix,
      monitorSend,
      meter: this.createMeterTap(),
      state: DEFAULT_STRIP,
      configured: false,
      requested: [],
      slots: new Map(),
      order: [],
      edges: [],
      mixTarget,routeEpoch:0,
      mixEndTime: 0,
    };
    this.strips.set(id, strip);
    this.levels[id] = strip.meter.reading;
    this.rewire(strip);
    if (this.workletsReady) this.attachMeter(strip.meter, fader);
    return strip;
  }

  observeProgramRoute(sourceId: Id) {
    const strip=this.strips.get(sourceId),source=strip?.source;
    if(!strip||!source||!this.workletsReady||strip.requested.some(f=>f.enabled)||strip.state.syncOffsetMs!==0)throw Error('program_route_unavailable');
    const generation=++this.routeGeneration,observed=new ProgramRouteObservation(generation);
    const node=new AudioWorkletNode(this.context,PROGRAM_ROUTE_PROCESSOR,{numberOfInputs:1,numberOfOutputs:1,outputChannelCount:[2],processorOptions:{generation}});
    let closed=false,failed=false;const state=strip.state,filters=strip.requested,routeEpoch=strip.routeEpoch,masterGeneration=this.masterGeneration;
    strip.programMix.disconnect(this.masterBus);strip.programMix.connect(node).connect(this.masterBus);
    node.port.onmessage=event=>observed.observe(event.data);node.onprocessorerror=()=>{failed=true;observed.clear();};
    const same=()=>!closed&&!failed&&this.strips.get(sourceId)===strip&&strip.source===source&&strip.state===state&&strip.requested===filters&&strip.routeEpoch===routeEpoch&&this.context.state==='running';
    const eligible=()=>same()&&this.masterTarget>0&&this.masterGeneration===masterGeneration&&!state.muted&&state.volume>0&&state.monitoring!=='monitor-only'&&strip.mixTarget>0&&this.context.currentTime>=strip.mixEndTime&&strip.programMix.gain.value>0&&strip.programSend.gain.value>0&&this.masterFader.gain.value>0&&Number.isFinite(this.masterFader.gain.value);
    const fresh=()=>observed.current(this.context.currentTime*this.context.sampleRate,this.context.sampleRate);
    const current=()=>eligible()&&!!fresh();
    return {current,observation:()=>current()?fresh():undefined,
      drained:()=>!closed&&!failed&&this.context.state==='running'&&observed.through()>Math.ceil(this.context.currentTime*this.context.sampleRate)-256&&!observed.current(),
      close:()=>{if(closed)return;closed=true;node.port.postMessage({type:'dispose'});node.port.onmessage=null;this.disconnectEdge(strip.programMix,node);node.disconnect();if(this.strips.get(sourceId)===strip)strip.programMix.connect(this.masterBus);}};
  }

  observeProgramOutput(){
    if(this.outputObserver||!this.workletsReady||this.destroyPromise||this.context.state!=='running')throw Error('program_output_observer_unavailable');
    const generation=++this.routeGeneration,observed=new ProgramRouteObservation(generation);
    const node=new AudioWorkletNode(this.context,PROGRAM_ROUTE_PROCESSOR,{numberOfInputs:1,numberOfOutputs:1,outputChannelCount:[2],processorOptions:{generation}});
    let closed=false,failed=false;
    this.masterFader.disconnect(this.programOut);this.masterFader.connect(node).connect(this.programOut);
    node.port.onmessage=event=>observed.observe(event.data);node.onprocessorerror=()=>{failed=true;observed.clear();owner.close();};
    const current=()=>!closed&&!failed&&!this.destroyPromise&&this.context.state==='running';
    const owner={current,observation:()=>current()?observed.interval(this.context.currentTime*this.context.sampleRate,this.context.sampleRate):undefined,close:()=>{
      if(closed)return;closed=true;node.onprocessorerror=null;try{node.port.postMessage({type:'dispose'});}catch{}node.port.onmessage=null;this.disconnectEdge(this.masterFader,node);node.disconnect();if(!this.destroyPromise)this.masterFader.connect(this.programOut);if(this.outputObserver===owner)this.outputObserver=undefined;
    }};
    this.outputObserver=owner;return owner;
  }

  private mixGainFor(id: Id): number {
    if (!this.programMix) return 1;
    return numberSetting(this.programMix[id], 0, 0, 1);
  }

  private detachSource(strip: Strip): void {
    if (!strip.source) return;
    // Only this edge: the source node may feed other consumers.
    this.disconnectEdge(strip.source, strip.input);
    strip.source = null;
  }

  private disconnectEdge(from: AudioNode, to: AudioNode): void {
    try {
      from.disconnect(to);
    } catch {
      // Already disconnected by its owner (e.g. a filter runtime tearing itself down).
    }
  }

  private applyStrip(strip: Strip, immediate: boolean): void {
    const { context } = this;
    const { state } = strip;
    const set = (param: AudioParam, value: number): void => {
      if (immediate) param.value = value;
      else smoothTo(context, param, value);
    };

    const delaySeconds = numberSetting(state.syncOffsetMs, 0, 0, MAX_SYNC_OFFSET_MS) / 1000;
    if (delaySeconds > strip.delayCapacity) this.growDelay(strip, delaySeconds);
    else set(strip.delay.delayTime, delaySeconds);

    const balance = numberSetting(state.balance, 0, -1, 1);
    const left = balance > 0 ? 1 - balance : 1;
    const right = balance < 0 ? 1 + balance : 1;
    if (state.forceMono) strip.matrix.set(0.5 * left, 0.5 * left, 0.5 * right, 0.5 * right, immediate);
    else strip.matrix.set(left, 0, 0, right, immediate);

    set(strip.fader.gain, state.muted ? 0 : numberSetting(state.volume, 1, 0, MAX_GAIN));
    set(strip.programSend.gain, state.monitoring === 'monitor-only' ? 0 : 1);
    set(strip.monitorSend.gain, state.monitoring === 'off' ? 0 : 1);
  }

  /** Replace the strip's DelayNode with one large enough for `seconds`. */
  private growDelay(strip: Strip, seconds: number): void {
    let capacity = strip.delayCapacity;
    while (capacity < seconds) capacity *= 2;
    capacity = Math.min(capacity, MAX_SYNC_OFFSET_MS / 1000);
    const delay = new DelayNode(this.context, { maxDelayTime: capacity, delayTime: seconds });
    const old = strip.delay;
    strip.input.disconnect(old);
    strip.input.connect(delay);
    strip.delay = delay;
    strip.delayCapacity = capacity;
    this.rewire(strip);
    old.disconnect();
  }

  // ---- filters ------------------------------------------------------------

  private reconcileFilters(strip: Strip): void {
    const order: FilterSlot[] = [];
    const kept = new Set<FilterSlot>();
    for (const instance of strip.requested) {
      const definition = this.deps.registry.getAudioFilter(instance.kind);
      if (!definition) continue;
      let slot = strip.slots.get(instance.id);
      if (slot && kept.has(slot)) continue;
      if (slot && slot.kind !== instance.kind) slot = undefined;
      if (!slot) {
        slot = {
          id: instance.id,
          kind: instance.kind,
          settings: instance.settings,
          resolved: { ...definition.defaults(), ...instance.settings },
          enabled: instance.enabled,
          runtime: null,
        };
        this.createRuntime(strip, slot, definition);
      } else if (slot.settings !== instance.settings) {
        slot.settings = instance.settings;
        slot.resolved = { ...definition.defaults(), ...instance.settings };
        this.updateRuntime(slot);
      }
      slot.enabled = instance.enabled;
      order.push(slot);
      kept.add(slot);
    }

    const removed = [...strip.slots.values()].filter((slot) => !kept.has(slot));
    strip.slots = new Map(order.map((slot) => [slot.id, slot]));
    strip.order = order;
    this.rewire(strip);
    // After rewiring, so a removed filter is out of the signal path before its nodes go away.
    for (const slot of removed) this.destroyRuntime(slot);
  }

  private createRuntime(strip: Strip, slot: FilterSlot, definition: AudioFilterDefinition): void {
    const createdWith = slot.resolved;
    const fail = (error: unknown): void => {
      this.deps.logger.log('error', LOG_SCOPE, `Audio filter "${slot.kind}" could not be created`, error);
    };
    const adopt = (runtime: AudioFilterRuntime): void => {
      // The filter (or its strip, or the engine) may be gone by the time an async create resolves.
      if (this.strips.get(strip.id) !== strip || strip.slots.get(slot.id) !== slot) {
        runtime.destroy();
        return;
      }
      slot.runtime = runtime;
      if (slot.resolved !== createdWith) this.updateRuntime(slot);
      this.rewire(strip);
    };

    try {
      const result = definition.create({ audioContext: this.context, logger: this.deps.logger }, createdWith);
      if (isThenable(result)) {
        // Audio keeps flowing through the current chain until the runtime exists.
        result.then(adopt, fail);
      } else {
        slot.runtime = result;
      }
    } catch (error) {
      fail(error);
    }
  }

  private updateRuntime(slot: FilterSlot): void {
    try {
      slot.runtime?.update(slot.resolved);
    } catch (error) {
      this.deps.logger.log('error', LOG_SCOPE, `Audio filter "${slot.kind}" rejected its settings`, error);
    }
  }

  private destroyRuntime(slot: FilterSlot): void {
    const runtime = slot.runtime;
    slot.runtime = null;
    try {
      runtime?.destroy();
    } catch (error) {
      this.deps.logger.log('error', LOG_SCOPE, `Audio filter "${slot.kind}" failed to shut down`, error);
    }
  }

  /**
   * Make the connections delay -> enabled, ready filters in order -> matrix.
   * Only edges that differ from the current wiring are touched, in one
   * synchronous step, so the path is never open for more than the render
   * quantum in which the change lands.
   */
  private rewire(strip: Strip): void {
    const edges: Edge[] = [];
    let previous: AudioNode = strip.delay;
    for (const slot of strip.order) {
      if (!slot.runtime || !slot.enabled) continue;
      edges.push([previous, slot.runtime.input]);
      previous = slot.runtime.output;
    }
    edges.push([previous, strip.matrix.input]);

    const same = (a: Edge, b: Edge): boolean => a[0] === b[0] && a[1] === b[1];
    for (const old of strip.edges) {
      if (!edges.some((edge) => same(edge, old))) this.disconnectEdge(old[0], old[1]);
    }
    for (const edge of edges) {
      if (!strip.edges.some((old) => same(edge, old))) edge[0].connect(edge[1]);
    }
    strip.edges = edges;
  }

  // ---- metering -----------------------------------------------------------

  private createMeterTap(): MeterTap {
    return {
      node: null,
      reading: silentReading(),
      clipHold: new ClipHold(CLIP_HOLD_MS, this.context.sampleRate),
      quanta: 0,
    };
  }

  private attachMeter(tap: MeterTap, from: AudioNode): void {
    const node = new AudioWorkletNode(this.context, METER_PROCESSOR, {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: WORKLET_CHANNELS,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
    });
    // Runs for every report whether or not anyone calls getLevels(), so readings
    // and the quanta count stay current for consumers that only poll occasionally.
    node.port.onmessage = (event: MessageEvent<Float64Array>) => {
      const clipped = applyMeterLevels(event.data, WORKLET_CHANNELS, tap.reading);
      tap.quanta = meterReportQuanta(event.data);
      tap.reading.clipped = tap.clipHold.update(tap.quanta, clipped);
    };
    node.onprocessorerror = () => this.deps.logger.log('error', LOG_SCOPE, 'Meter processor failed');
    from.connect(node);
    tap.node = node;
  }

  private detachMeter(tap: MeterTap): void {
    const node = tap.node;
    if (!node) return;
    tap.node = null;
    const dispose: WorkletMessage<never> = { type: 'dispose' };
    node.port.postMessage(dispose);
    node.port.onmessage = null;
    node.onprocessorerror = null;
    node.disconnect();
  }

  private clearAllReadings(): void {
    // Includes the clip hold: it is timed by meter reports, which have stopped.
    const clear = (tap: MeterTap): void => {
      tap.reading.peakDb.fill(-Infinity);
      tap.reading.rmsDb.fill(-Infinity);
      tap.reading.clipped = false;
      tap.clipHold.reset();
    };
    clear(this.masterMeter);
    for (const strip of this.strips.values()) clear(strip.meter);
  }

  // ---- monitoring ---------------------------------------------------------

  /** The monitor device is only kept open while something is actually routed to it. */
  private updateMonitorPlayback(): void {
    if (this.destroyPromise) return;
    let wanted = false;
    for (const strip of this.strips.values()) {
      if (strip.state.monitoring !== 'off') wanted = true;
    }
    this.monitor.update(wanted && this.context.state === 'running');
  }
}

export function createAudioEngine(deps: AudioEngineDeps): AudioEngine {
  return new AudioEngineImpl(deps);
}
